"use client"

import { useCallback, useState } from "react"
import dynamic from "next/dynamic"
import Image from "next/image"
import { Heart, Info, MapPin, MoreHorizontal, Star } from "lucide-react"
import { random } from "radash"

import { RestaurantStatus } from "@/lib/types"
import { GetRelatedRestoResponse, RestoDetailData } from "@/lib/types/responses"
import { getPlaceHolderDishImage } from "@/lib/utils"
import { useRestaurantLike } from "@/hooks/use-restaurant-like"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { RestoCloseStatus } from "@/components/resto-close-status"
import { LikeButton } from "@/components/resto-like-button"

import { RelatedRestaurantsSkeleton } from "./menu-skeleton"
import { RestaurantInfoDialog } from "./restaurant-info-dialog"

// Lazy load the RelatedRestaurants component with matching skeleton
// to prevent layout shifts while maintaining consistent loading UI
const RelatedRestaurants = dynamic(
  () => import("./related-restaurants").then((mod) => mod.RelatedRestaurants),
  {
    ssr: false,
    loading: () => <RelatedRestaurantsSkeleton />,
  }
)

interface RestaurantHeaderProps {
  restaurant: RestoDetailData
  tier: {
    name: string
    logo: string
  }
  hasGeo?: boolean
  relatedRestoPromise?: Promise<GetRelatedRestoResponse>
}

export function RestaurantHeader({
  restaurant,
  hasGeo = false,
  relatedRestoPromise,
}: RestaurantHeaderProps) {
  const {
    name,
    image_compressed_web,
    image,
    address,
    rating,
    review_count,
    distance_text,
    is_closed,
    end_time,
    next_slot_time,
    restaurant_id,
    status,
  } = restaurant.restaurant_info

  const [showInfoDialog, setShowInfoDialog] = useState(false)
  const { isLiked, toggleFavorite } = useRestaurantLike(restaurant_id)

  const handleScrollToRatingSection = useCallback(() => {
    const ratingSection = document.getElementById("rating-section")
    if (ratingSection) {
      // Calculate header height or any fixed elements
      const headerOffset = 80 // Adjust based on your header height
      const elementPosition = ratingSection.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      })
    }
  }, [])

  return (
    <header className="mb-8" role="banner">
      <div className="container mx-auto px-5 lg:px-8 xl:px-12">
        <div className="flex flex-col gap-3">
          <div className="relative w-full h-[180px] lg:h-[240px] mb-4 overflow-hidden rounded-lg mt-1.5">
            <Image
              src={
                image ||
                image_compressed_web ||
                "/images/restaurant-placeholder.jpg"
              }
              alt={`Hình ảnh ${name}`}
              fill
              priority
              placeholder="blur"
              blurDataURL={getPlaceHolderDishImage()}
              quality={99}
              sizes="100vw"
              className="object-cover w-full h-full"
              itemProp="image"
            />

            <div className="flex items-center gap-2 absolute top-4 right-4">
              <LikeButton
                restaurantId={restaurant_id}
                compact
                className="bg-white shadow-[0px_10px_24px_0px_rgba(0,0,0,0.16)]"
              />

              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="bg-white size-8 rounded-full border-0 shadow-[0px_10px_24px_0px_rgba(0,0,0,0.16)]"
                    aria-label="Thêm tùy chọn"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-2" align="end">
                  <div className="flex flex-col">
                    <Button
                      variant="ghost"
                      className="flex items-center justify-start gap-2 h-10"
                      onClick={toggleFavorite}
                    >
                      <Heart
                        className={`h-4 w-4 ${isLiked ? "fill-rose-500 text-rose-500" : ""}`}
                      />
                      <span>{isLiked ? "Bỏ yêu thích" : "Yêu thích"}</span>
                    </Button>

                    <Button
                      variant="ghost"
                      className="flex items-center justify-start gap-2 h-10"
                      onClick={() => setShowInfoDialog(true)}
                    >
                      <Info className="h-4 w-4" />
                      <span>Thông tin quán</span>
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="flex items-center gap-2 justify-between">
            <div className="flex gap-2 items-center">
              <h1
                className="text-2xl md:text-2xl font-semibold text-gray-900"
                itemProp="name"
              >
                {name}
              </h1>
            </div>
          </div>

          <p
            className="text-zinc-700 text-base font-light font-be-vietnam"
            itemProp="address"
          >
            {address}
          </p>

          <RestoCloseStatus
            status={status as RestaurantStatus}
            isClosed={is_closed}
            endTime={end_time as unknown as string}
            nextSlotTime={next_slot_time as unknown as string}
          />

          <div className="flex flex-wrap gap-4 text-sm font-be-vietnam items-center">
            {review_count >= 1 && (
              <div
                className="flex items-center gap-1 border border-gray-200 rounded-lg px-2 py-1 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={handleScrollToRatingSection}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    handleScrollToRatingSection()
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label="Xem đánh giá"
              >
                <Star size={14} className="text-amber-400 fill-amber-400" />
                <span
                  className="text-gray-700"
                  itemProp="aggregateRating"
                  itemScope
                  itemType="https://schema.org/AggregateRating"
                >
                  <meta
                    itemProp="ratingValue"
                    content={(rating || 5).toFixed(1)}
                  />
                  <meta itemProp="bestRating" content="5" />
                  <meta itemProp="worstRating" content="1" />
                  <meta
                    itemProp="reviewCount"
                    content={(review_count || random(5, 20)).toString()}
                  />
                  <span className="font-bold">{rating.toFixed(1)} </span>
                  <span className="text-gray-500 text-xs mr-1">|</span>
                  <span className="text-blue-600">
                    {review_count > 999 ? "999+" : review_count} đánh giá
                  </span>
                </span>
              </div>
            )}

            {hasGeo && (
              <div
                className="flex items-center gap-1"
                itemProp="geo"
                itemScope
                itemType="https://schema.org/GeoCoordinates"
              >
                <MapPin size={16} className="text-gray-800" />
                <span className="text-gray-700">{distance_text}</span>
              </div>
            )}

            {/* <button
              className="text-gray-500 hover:text-gray-700 cursor-pointer underline font-be-vietnam bg-transparent border-none p-0 text-left"
              onClick={() => setShowInfoDialog(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  setShowInfoDialog(true)
                }
              }}
            >
              Thông tin quán
            </button> */}
          </div>

          {relatedRestoPromise && (
            <RelatedRestaurants relatedRestoPromise={relatedRestoPromise} />
          )}

          {/* Restaurant Info Dialog */}
          <RestaurantInfoDialog
            open={showInfoDialog}
            onOpenChange={setShowInfoDialog}
            restaurantId={restaurant_id}
          />
        </div>
      </div>
    </header>
  )
}
